"use client";
import {
  GetClientById,
  GetClientStatsById,
  GetDatesCountOverTime,
} from "@/app/actions/clients/actions";
import NumberTicker from "@/components/magicui/number-ticker";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { Tables } from "@/types/supabase";
import { axisClasses } from "@mui/x-charts/ChartsAxis";
import { LineChart, lineElementClasses } from "@mui/x-charts/LineChart";
import {
  Avatar,
  Badge,
  Box,
  Code,
  DataList,
  Flex,
  Grid,
  Heading,
  Link as RLink,
} from "@radix-ui/themes";
import { average } from "color.js";
import {
  ContactRound,
  Delete,
  Edit,
  EllipsisVertical,
  FileClock,
  FileSignature,
  HandCoins,
} from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";

export type ClientWithContacts = Tables<"clients"> & {
  contacts: Tables<"contacts">[];
  contracts: Tables<"contracts">[];
  timesheets: Tables<"timesheets">[];
};

export type ClientWithStats = {
  value: number | undefined;
  contacts: { count: number }[];
  contracts: { count: number }[];
  timesheets: { count: number }[];
};

export default function View({ params }: { params: { clientid: string } }) {
  const [activeClient, setActiveClient] = useState<ClientWithContacts | null>(
    null,
  );
  const [clientStats, setClientStats] = useState<ClientWithStats | null>(null);
  const [datesOverTime, setDatesOverTime] = useState<
    {
      week: string;
      dates: number;
    }[]
  >([]);
  // const [chartReady, setChartReady] = useState(false);

  useEffect(() => {
    GetClientById(params.clientid).then(({ data, error }) => {
      if (error) {
        console.log(error);
      }
      if (data) {
        setActiveClient(data);

        data?.logo &&
          average(data.logo).then((color) => {
            console.log("COLORS", color);
          });
      }
      console.log(data);
    });

    GetClientStatsById(params.clientid).then(({ data, error }) => {
      if (error) {
        console.log(error);
      }
      if (data) {
        setClientStats(data);
      }
      console.log(data);
    });

    GetDatesCountOverTime(params.clientid).then(({ data, error }) => {
      if (error) {
        console.log(error);
      }
      if (data) {
        console.log(data);
        setDatesOverTime(data);
        // setChartReady(true);
      }
    });
  }, [params.clientid]);

  const getXAxis = () => {
    console.log(datesOverTime);
    return datesOverTime.map((d) => new Date(d.week));
  };

  const getYAxis = () => {
    return datesOverTime.map((d) => d.dates);
  };

  return (
    <Flex
      gap="3"
      direction="column"
      align="center"
      className="w-full justify-self-start min-h-full"
    >
      <Flex className="w-full">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/clients">Clients</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{activeClient?.name}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </Flex>

      <Card className="w-full">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex flex-row items-center">
            {!activeClient && <Skeleton className="w-[50px] h-[50px]" />}
            {activeClient && (
              <Avatar
                size="3"
                src={activeClient.logo ? activeClient.logo : ""}
                fallback={activeClient.name[0]}
                className="mr-3"
              />
            )}
            {!activeClient && <Skeleton className="w-[50px] h-[50px]" />}
            {activeClient && <Heading size="5">{activeClient.name}</Heading>}
          </CardTitle>
          <DropdownMenu>
            <DropdownMenuTrigger className="flex justify-center">
              <EllipsisVertical className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent collisionPadding={15}>
              <DropdownMenuItem asChild>
                <Link href={`/contracts/create?client=${activeClient?.id}`}>
                  <FileSignature className="mr-2 h-4 w-4" />
                  New Contract
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <FileClock className="mr-2 h-4 w-4" />
                New Timesheet
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Delete className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </CardHeader>
        <CardContent>
          <Flex className="max-w-screen-xl" width="100%">
            <Grid columns="2fr 1.5fr" width="100%" gap="6">
              <DataList.Root>
                <DataList.Item align="center">
                  <DataList.Label minWidth="88px">Status</DataList.Label>
                  <DataList.Value>
                    <Badge color="jade" variant="soft" radius="full">
                      Live
                    </Badge>
                  </DataList.Value>
                </DataList.Item>
                <DataList.Item>
                  <DataList.Label minWidth="88px">ID</DataList.Label>
                  <DataList.Value>
                    <Flex align="center" gap="2">
                      <Code variant="ghost">{activeClient?.id}</Code>
                    </Flex>
                  </DataList.Value>
                </DataList.Item>
                <DataList.Item>
                  <DataList.Label minWidth="88px">Lead Contact</DataList.Label>
                  <DataList.Value>
                    {activeClient?.contacts[0].first_name}{" "}
                    {activeClient?.contacts[0].last_name}
                  </DataList.Value>
                </DataList.Item>
                <DataList.Item>
                  <DataList.Label minWidth="88px">Contact Email</DataList.Label>
                  <DataList.Value>
                    <Link href={`mailto:${activeClient?.contacts[0].email}`}>
                      {activeClient?.contacts[0].email}
                    </Link>
                  </DataList.Value>
                </DataList.Item>
                <DataList.Item>
                  <DataList.Label minWidth="88px">Website</DataList.Label>
                  <DataList.Value>
                    <Link target="_blank" href="https://website.com">
                      {activeClient?.name}
                    </Link>
                  </DataList.Value>
                </DataList.Item>
              </DataList.Root>

              <Box width="100%">
                <Flex direction="row" justify="between" align="start">
                  <Heading size="4" mb="4">
                    Lead Contact
                  </Heading>
                  <RLink asChild size="2">
                    <Link href={`/clients/${activeClient?.id}/contacts`}>
                      View All
                    </Link>
                  </RLink>
                </Flex>
                <Card className="p-4">
                  <DataList.Root orientation="vertical" size="3">
                    <DataList.Item>
                      <DataList.Label minWidth="88px">Name</DataList.Label>
                      <DataList.Value>
                        <Flex align="center" gap="2">
                          <Avatar
                            fallback={
                              activeClient?.contacts[0].first_name[0] +
                              "" +
                              activeClient?.contacts[0].last_name[0]
                            }
                            size="2"
                          />
                          {activeClient?.contacts[0].first_name}{" "}
                          {activeClient?.contacts[0].last_name}
                        </Flex>
                      </DataList.Value>
                    </DataList.Item>
                    <DataList.Item>
                      <DataList.Label minWidth="88px">Email</DataList.Label>
                      <DataList.Value>
                        {!activeClient?.contacts[0].email && (
                          <Skeleton className="w-[150px] h-5" />
                        )}
                        {activeClient?.contacts[0].email && (
                          <RLink
                            href={`mailto:${activeClient?.contacts[0].email}`}
                          >
                            {activeClient?.contacts[0].email}
                          </RLink>
                        )}
                      </DataList.Value>
                    </DataList.Item>
                  </DataList.Root>
                </Card>
              </Box>
            </Grid>
          </Flex>
          <Grid columns="4" className="w-full mt-8" gap="4">
            <Card className="">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 p-4 pb-2 ">
                <CardTitle className="text-sm font-medium">Contracts</CardTitle>
                <FileSignature className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <p className="whitespace-pre-wrap text-2xl font-bold tracking-tighter text-black dark:text-white">
                  <NumberTicker
                    startValue={0}
                    endValue={clientStats?.contracts[0]?.count || 0}
                    duration={1}
                  />
                </p>
                <p className="text-xs text-muted-foreground">23 last 30 days</p>
              </CardContent>
            </Card>

            <Card className="relative max-h-[150px] overflow-hidden">
              <div className="absolute top-0 -left-[15px] right-0 bottom-0 overflow-hidden rounded-md z-10 scale-110 origin-bottom opacity-15">
                <LineChart
                  xAxis={[
                    {
                      id: "Weeks",
                      data: getXAxis(),
                      scaleType: "time",
                      valueFormatter: (date: Date) =>
                        date.toLocaleDateString("en-GB"),
                    },
                  ]}
                  margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                  series={[
                    {
                      data: getYAxis(),
                      id: "value",
                      area: true,
                      color: "black",
                      showMark: false,
                    },
                  ]}
                  sx={{
                    [`& .${lineElementClasses.root}`]: {
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      strokeWidth: 0,
                    },
                    "& .MuiAreaElement-series-value": {
                      fill: "url('#dateGradient')",
                    },
                    [`.${axisClasses.root}`]: {
                      [`.${axisClasses.tick}, .${axisClasses.line}`]: {
                        display: "none",
                      },
                      [`.${axisClasses.tickLabel}`]: {
                        display: "none",
                      },
                      [`.${axisClasses.bottom}`]: {
                        display: "none",
                      },
                    },
                  }}
                >
                  <defs>
                    <linearGradient
                      id="dateGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="black" />
                      <stop offset="95%" stopColor="grey" />
                    </linearGradient>
                  </defs>
                </LineChart>
              </div>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 p-4 pb-2 z-20 relative">
                <CardTitle className="text-sm font-medium">
                  Timesheets
                </CardTitle>
                <FileClock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent className="p-4 pt-0 z-20 relative">
                <p className="whitespace-pre-wrap text-2xl font-bold tracking-tighter text-black dark:text-white">
                  <NumberTicker
                    startValue={0}
                    endValue={clientStats?.timesheets[0]?.count || 0}
                    duration={1}
                  />
                </p>
                <p className="text-xs text-muted-foreground">23 last 30 days</p>
              </CardContent>
            </Card>

            <Card className="">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 p-4 pb-2 ">
                <CardTitle className="text-sm font-medium">Contacts</CardTitle>
                <ContactRound className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <p className="whitespace-pre-wrap text-2xl font-bold tracking-tighter text-black dark:text-white">
                  <NumberTicker
                    startValue={0}
                    endValue={clientStats?.contacts[0]?.count || 0}
                    duration={1}
                  />
                </p>
                <p className="text-xs text-muted-foreground">23 last 30 days</p>
              </CardContent>
            </Card>

            <Card className="">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 p-4 pb-2 ">
                <CardTitle className="text-sm font-medium">
                  Total Earned
                </CardTitle>
                <HandCoins className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <p className="whitespace-pre-wrap text-2xl font-bold tracking-tighter text-black dark:text-white">
                  £
                  <NumberTicker
                    startValue={0}
                    endValue={clientStats?.value || 0}
                    duration={1}
                  />
                </p>
                <p className="text-xs text-muted-foreground">
                  Since 20/05/2023
                </p>
              </CardContent>
            </Card>
          </Grid>
        </CardContent>
      </Card>
    </Flex>
  );
}
